import { useState, useCallback, useMemo } from 'react';
import { useEvent } from "react-use-event-hook";
import { createContext, useContextSelector } from "use-context-selector";
import { useLocation } from "react-router";
import { publicAction } from '@/data/loaders';
import { refreshUserdata, useUser } from '@/systems/Authentication';
import { useQuery } from "@tanstack/react-query";

export const useStorage = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    return localStorage.getItem(key) !== null ? JSON.parse(localStorage.getItem(key)) : undefined
  });

  const return_value = value !== undefined ? value : defaultValue;

  const updateValue = useEvent((fn_or_value) => {
    let current_value = localStorage.getItem(key) !== null ? JSON.parse(localStorage.getItem(key)) : undefined;
    let new_value = typeof fn_or_value == "function" ? fn_or_value(current_value !== undefined ? current_value : defaultValue) : fn_or_value;
    setValue(new_value);
    localStorage.setItem(key, JSON.stringify(new_value));
    return new_value;
  });

  return [return_value, updateValue];
}

const InmemoryStorageContext = createContext({});

export const InmemoryStorageProvider = ({ children }) => {
  const [storage, setStorage] = useState({});

  const setValue = useCallback((key, value) => {
    setStorage(current => ({ ...current, [key]: value }));
  }, []);

  const contextValue = useMemo(() => ({ storage, setValue }), [storage, setValue]);

  return (
    <InmemoryStorageContext.Provider value={contextValue}>
      {children}
    </InmemoryStorageContext.Provider>
  );
};

export const useInmemoryStorage = (key, defaultValue) => {
  const value = useContextSelector(InmemoryStorageContext, ctx => ctx.storage[key]);
  const setValue = useContextSelector(InmemoryStorageContext, ctx => ctx.setValue);

  const return_value = value !== undefined ? value : defaultValue;

  const updateValue = useEvent((fn_or_value) => {
    const new_value = typeof fn_or_value === "function" ? fn_or_value(return_value) : fn_or_value;
    setValue(key, new_value);
    return new_value;
  });

  return [return_value, updateValue];
};

export const useLocationStorage = (key, defaultValue) => {
  const location = useLocation();
  const scopedKey = `${location.pathname}:${location.key || "default"}:${key}`;
  return useInmemoryStorage(scopedKey, defaultValue);
};

export const usePreference = (key, defaultValue) => {
  const { userdata } = useUser();
  const server_value = userdata.prefs?.[key] != null ? userdata.prefs?.[key] : undefined;

  const value = useContextSelector(InmemoryStorageContext, ctx => ctx.storage["pref."+key]);
  const setValue = useContextSelector(InmemoryStorageContext, ctx => ctx.setValue);

  const return_value = value !== undefined ? value : (server_value !== undefined ? server_value : defaultValue);
  const server_compare_value = server_value !== undefined ? server_value : defaultValue;

  useQuery({
    queryKey: ["preference", key, return_value, server_compare_value],
    queryFn: () => {
      return publicAction("updatePreferences", { prefs: { [key]: return_value } }).then(() => {
        refreshUserdata();
        return true;
      })
    },
    enabled: JSON.stringify(return_value) !== JSON.stringify(server_compare_value)
  });

  const updateValue = useEvent((fn_or_value) => {
    let new_value = typeof fn_or_value == "function" ? fn_or_value(return_value) : fn_or_value;
    setValue("pref."+key, new_value);
    return new_value;
  });

  return [return_value, updateValue];
}